/**
 * How a TCG Pocket card says it is free.
 *
 * TCG Pocket cards are digital and cost nothing to collect inside the Pocket
 * app. Earlier these screens framed that as a pricing failure — an empty price,
 * an empty ladder, and a line explaining that the card was never printed — and
 * an explanation of an absence reads as an apology. The fact itself is good
 * news, so the word in the price slot is "Free" and the sentence under it
 * points at the app rather than at the missing number.
 *
 * Three shapes, one message, so the same words land wherever a price would
 * have been:
 *   TcgPocketBanner  the block that replaces the pricing section
 *   TcgPocketChip    the badge that replaces a price on a thumbnail
 *   TcgPocketLine    the one-liner that replaces a set or era total
 *
 * Purple, from the existing palette. Not the cyan accent, which means "tap
 * me", and not amber or red, which mean something went wrong.
 */
import { Smartphone } from 'lucide-react';
import {
  TCG_POCKET_LABEL,
  TCG_POCKET_PRICE_LABEL,
  TCG_POCKET_REASON,
  TCG_POCKET_SHORT,
} from '../utils/tcgPocket';

/** Purple tuned per theme: the token tints are built for chips, not panels. */
const tones = (theme, isDark) => ({
  ink: theme.purple,
  fill: isDark ? 'rgba(139, 92, 246, 0.10)' : 'rgba(124, 58, 237, 0.07)',
  edge: isDark ? 'rgba(139, 92, 246, 0.32)' : 'rgba(124, 58, 237, 0.28)',
  body: theme.textMuted,
});

/**
 * The block that stands where the pricing section would, so the page keeps the
 * same rhythm as any other card.
 *
 * `title` and `reason` swap the words without swapping the shape, for the
 * places where the fact that matters is not the default one — the card's own
 * screen leads with the invitation to download the app, and the trading
 * surfaces lead with where a swap actually happens. Same badge, same colour,
 * different fact.
 */
export function TcgPocketBanner({
  theme,
  isDark = true,
  title = TCG_POCKET_LABEL,
  reason = TCG_POCKET_REASON,
  style,
}) {
  const t = tones(theme, isDark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 14px',
        borderRadius: '12px',
        border: `1px solid ${t.edge}`,
        backgroundColor: t.fill,
        ...style,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto',
          width: '34px',
          height: '34px',
          borderRadius: '9px',
          backgroundColor: isDark ? 'rgba(139, 92, 246, 0.16)' : 'rgba(124, 58, 237, 0.12)',
          color: t.ink,
        }}
      >
        <Smartphone size={18} strokeWidth={2} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
        <span
          style={{
            fontSize: '13.5px',
            fontWeight: '700',
            letterSpacing: '0.2px',
            color: t.ink,
            lineHeight: 1.2,
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: '12.5px', lineHeight: 1.45, color: t.body }}>
          {reason}
        </span>
      </span>
    </div>
  );
}

/**
 * The thumbnail badge. Sits exactly where the green price badge sits, in the
 * same shape and the same size, so a mixed grid still scans as one grid.
 *
 * It says "Free" because it occupies the price slot, and the question a price
 * slot answers is what the card costs. `label` is there for a caller that
 * genuinely needs the product's name in that spot instead.
 */
export function TcgPocketChip({ theme, isDark = true, small = false, label = TCG_POCKET_PRICE_LABEL, style }) {
  const t = tones(theme, isDark);
  return (
    <div
      style={{
        position: 'absolute',
        bottom: small ? '4px' : '4px',
        right: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: small ? '2px 4px' : '3px 6px',
        borderRadius: small ? '3px' : '4px',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        color: t.ink,
        fontWeight: '600',
        fontSize: small ? '8px' : '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        ...style,
      }}
    >
      <span style={{ lineHeight: 1 }}>{label}</span>
    </div>
  );
}

/**
 * The one-liner that replaces a set or era value. It NAMES the product rather
 * than pricing it.
 *
 * This used to lead with "Free", matching the chip. On a single card that is
 * exactly right - the chip sits where a price would, and free is the honest
 * answer. In a grid of sets it is not: the tiles around it read "Set value
 * $984.79", and a tile reading "Free" answers a question nobody asked while
 * leaving the real one open. The owner, looking at that grid: "instead of free
 * cannot just say TCG pocket?". Naming the product says why there is no number.
 *
 * `compact` drops the trailing clause for rows that only have a line of height,
 * such as a set tile. `label` and `note` stay swappable for the surfaces that
 * need a different fact - the card screen's trade row is one.
 */
export function TcgPocketLine({
  theme,
  isDark = true,
  compact = false,
  label = TCG_POCKET_LABEL,
  // With the product named up front, the old clause ("digital cards from TCG
  // Pocket") only said it twice. The free part is the good news and it moves
  // here, so the full line reads "TCG Pocket - Free in the app".
  note = TCG_POCKET_SHORT,
  style,
}) {
  const t = tones(theme, isDark);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        minWidth: 0,
        color: t.ink,
        fontSize: compact ? '10px' : '12px',
        fontWeight: '600',
        lineHeight: 1.3,
        ...style,
      }}
    >
      <Smartphone size={compact ? 10 : 13} strokeWidth={2.2} style={{ flex: '0 0 auto' }} />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </span>
      {!compact && (
        <span style={{ color: t.body, fontWeight: '500', whiteSpace: 'nowrap' }}>
          {`· ${note}`}
        </span>
      )}
    </span>
  );
}

export default TcgPocketBanner;
